import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CreateDMOInvitationPayload,
  DMOInvitation,
  DMOInvitationListQueryParams,
  DMORegisteredListQueryParams,
  DMORegisteredProfile,
} from "@/types/dmo-management.types";

interface RawDMORegisteredPaginatedResponse
  extends PaginatedApiResponse<DMORegisteredProfile[]> {
  total_items?: number;
  total_pages?: number;
  current_page?: number;
}

export const DMOManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDMOInvitations: builder.query<
      PaginatedApiResponse<DMOInvitation[]>,
      DMOInvitationListQueryParams
    >({
      query: (params) => ({
        url: "dmo-registration/invitations",
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
        },
      }),
      transformResponse: (response: PaginatedApiResponse<DMOInvitation[]>) => {
        return {
          ...response,
          totalItems: response.totalItems ?? response.total_items,
          totalPages: response.totalPages ?? response.total_pages,
          currentPage: response.currentPage ?? response.current_page,
        } as PaginatedApiResponse<DMOInvitation[]>;
      },
      providesTags: ["DMOInvitations"],
      keepUnusedDataFor: 0,
    }),

    getRegisteredDMOs: builder.query<
      PaginatedApiResponse<DMORegisteredProfile[]>,
      DMORegisteredListQueryParams
    >({
      query: (params) => ({
        url: "dmo-registration/registered-dmos",
        method: "GET",
        params: {
          user_id: params.user_id,
          search: params.search,
          page: params.page,
          limit: params.limit,
          language: params.language,
        },
        headers: {
          "x-lang": params.language || "en",
          ...(params["x-session-id"] && {
            "x-session-id": params["x-session-id"],
          }),
          ...(params["x-location-name"] && {
            "x-location-name": params["x-location-name"],
          }),
          ...(typeof params["x-latitude"] === "number" && {
            "x-latitude": params["x-latitude"].toString(),
          }),
          ...(typeof params["x-longitude"] === "number" && {
            "x-longitude": params["x-longitude"].toString(),
          }),
        },
      }),
      transformResponse: (
        response: RawDMORegisteredPaginatedResponse,
      ): PaginatedApiResponse<DMORegisteredProfile[]> => {
        return {
          ...response,
          totalItems: response.totalItems ?? response.total_items,
          totalPages: response.totalPages ?? response.total_pages,
          currentPage: response.currentPage ?? response.current_page ?? 1,
        };
      },
      keepUnusedDataFor: 0,
    }),

    createDMOInvitation: builder.mutation<
      ApiResponse<DMOInvitation>,
      CreateDMOInvitationPayload
    >({
      query: (data) => ({
        url: "dmo-registration/invitations",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<DMOInvitation>) => response,
      invalidatesTags: ["DMOInvitations"],
    }),

    getDMOInvitationById: builder.query<DMOInvitation, string>({
      query: (id) => ({
        url: `dmo-registration/invitations/by-id/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown): DMOInvitation => {
        const anyResponse = response as
          | ApiResponse<DMOInvitation>
          | DMOInvitation;
        if ((anyResponse as ApiResponse<DMOInvitation>).data) {
          return (anyResponse as ApiResponse<DMOInvitation>).data;
        }
        return anyResponse as DMOInvitation;
      },
      providesTags: (result, error, id) => [{ type: "DMOInvitations", id }],
      keepUnusedDataFor: 0,
    }),

    cancelDMOInvitation: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `dmo-registration/invitations/${id}/cancel`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["DMOInvitations"],
    }),

    directDMORegistration: builder.mutation<
      ApiResponse<unknown>,
      { data: FormData; lang?: string }
    >({
      query: ({ data, lang = "en" }) => ({
        url: "dmo-registration/direct-registration",
        method: "POST",
        data,
        headers: {
          "Content-Type": "multipart/form-data",
          "x-lang": lang,
        },
      }),
      transformResponse: (response: ApiResponse<unknown>) => response,
    }),

    getDMOInvitationByToken: builder.query<
      ApiResponse<{ email: string; name?: string }>,
      string
    >({
      query: (token) => ({
        url: `dmo-registration/invitations/${token}`,
        method: "GET",
      }),
    }),

    completeDMORegistration: builder.mutation<
      ApiResponse<unknown>,
      { token: string; data: FormData; lang?: string }
    >({
      query: ({ token, data, lang = "en" }) => ({
        url: `dmo-registration/invitations/${token}/complete`,
        method: "POST",
        data,
        headers: {
          "Content-Type": "multipart/form-data",
          "x-lang": lang,
        },
      }),
      transformResponse: (response: ApiResponse<unknown>) => response,
    }),

    getRegisteredDMOById: builder.query<DMORegisteredProfile, string>({
      query: (id) => ({
        url: `dmo-registration/registered-dmos/by-id/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown): DMORegisteredProfile => {
        const anyResponse = response as
          | ApiResponse<DMORegisteredProfile>
          | DMORegisteredProfile;
        if ((anyResponse as ApiResponse<DMORegisteredProfile>).data) {
          return (anyResponse as ApiResponse<DMORegisteredProfile>).data;
        }
        return anyResponse as DMORegisteredProfile;
      },
      providesTags: (result, error, id) => [{ type: "DMORegistered", id }],
      keepUnusedDataFor: 0,
    }),

    updateRegisteredDMO: builder.mutation<
      ApiResponse<unknown>,
      { id: string; data: FormData; lang?: string }
    >({
      query: ({ id, data, lang = "en" }) => ({
        url: `dmo-registration/registered-dmos/${id}`,
        method: "PATCH",
        data,
        headers: {
          "Content-Type": "multipart/form-data",
          "x-lang": lang,
        },
      }),
      transformResponse: (response: ApiResponse<unknown>) => response,
      invalidatesTags: (result, error, { id }) => [
        { type: "DMORegistered", id },
      ],
    }),

    deleteRegisteredDMO: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `dmo-registration/registered-dmos/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["DMORegistered"],
    }),
  }),
});

export const {
  useGetDMOInvitationsQuery,
  useCreateDMOInvitationMutation,
  useGetDMOInvitationByIdQuery,
  useCancelDMOInvitationMutation,
  useDirectDMORegistrationMutation,
  useGetRegisteredDMOsQuery,
  useGetDMOInvitationByTokenQuery,
  useCompleteDMORegistrationMutation,
  useGetRegisteredDMOByIdQuery,
  useUpdateRegisteredDMOMutation,
  useDeleteRegisteredDMOMutation,
} = DMOManagementService;
